export interface TopicItem {
  id: string;
  name: string;
  desc: string;
}

export interface SuggestionItem {
  id: string;
  text: string;
}

export interface PredictionMarketReply {
  text: string;
  followups: string[];
}

export const PREDICTION_MARKET_TOPICS: TopicItem[] = [
  { id: "creator", name: "AI Market Creator", desc: "Markets generated from live data feeds" },
  { id: "resolver", name: "AI Market Resolver", desc: "Outcomes settled with a full audit trail" },
  { id: "sportiq", name: "SportIQ", desc: "Sports markets priced in real time" },
  { id: "amm", name: "AMM and Liquidity", desc: "Pricing, depth and risk controls" }
];

export const PRODUCTS_AND_SERVICES_TOPICS: TopicItem[] = [
  { id: "/prediction-market-software", name: "Prediction Market Software", desc: "White-label trading & event contracts" },
  { id: "/audience-engagement-platform", name: "Interactive Fan Zones", desc: "Gamified hubs for leagues, clubs & media" },
  { id: "/fantasy-sports-software-development", name: "Fantasy Sports Solutions", desc: "Daily fantasy, season-long & gaming software" },
  { id: "/white-label-sports-predictor-games", name: "Our Games Catalog", desc: "Turnkey sports predictor games & mechanics" },
  { id: "/custom-game-development", name: "Game Development for iGaming", desc: "Custom games, sportsbook engines & player platforms" }
];


export const PREDICTION_MARKET_LABELS: Record<string, string> = {
  creator: "How are markets created automatically?",
  resolver: "How does automatic settlement work?",
  sportiq: "What does SportIQ actually do?",
  amm: "AMM or order book, which fits us?",
  launch: "How long does a white label launch take?",
  broker: "What do brokerages need for event contracts?",
  ownership: "Do we own the source code?",
  coverage: "Which market categories can we cover?"
};

export const PREDICTION_MARKET_SUGGESTIONS: SuggestionItem[] = [
  { id: "launch", text: "How long does a white label launch take?" },
  { id: "amm", text: "AMM or order book, which model fits us?" },
  { id: "sportiq", text: "What does SportIQ actually do?" }
];

export const PREDICTION_MARKET_REPLIES: Record<string, PredictionMarketReply> = {
  creator: {
    text: "AI Market Creator turns a data feed into tradable markets, so nobody writes each one by hand.\n\nHow it works:\n• You connect a source: a sports feed, a price feed, an elections or awards calendar, or your own event schedule.\n• The engine proposes markets from templates you approve, binary, multi outcome or scalar, with the question, outcome definitions and resolution source attached.\n• Opening probabilities come from the model, then move with order flow.\n• Everything can land in a review queue first if you want a human gate before markets go live.\n\nOperators usually start with templates for their two or three core categories, then widen once volume justifies it.",
    followups: ["resolver", "coverage", "launch"]
  },
  resolver: {
    text: "AI Market Resolver closes the loop after a market ends. It decides the outcome, settles positions and keeps the evidence.\n\nWhat it covers:\n• Resolution against the source declared when the market was created, so the rule is never decided after the fact.\n• Automatic settlement and payout, including partial and void cases.\n• A dispute window with an operator override, plus a full audit trail of who resolved what and on what evidence.\n• Defined handling for postponed, abandoned or ambiguous events.\n\nFor sports this runs seconds after the whistle. For slower categories it waits on the declared source.",
    followups: ["creator", "sportiq", "broker"]
  },
  sportiq: {
    text: "SportIQ is our sports prediction market engine. It generates, prices and resolves markets in real time from live match data, and runs continuously across fixtures and leagues without operator intervention.\n\nIn practice:\n• Pre match and in play markets are created per fixture from the templates you enable.\n• Prices update on every meaningful event in the feed, with risk and exposure limits applied automatically.\n• Markets settle from the same feed once the event resolves.\n• Coverage extends league by league as you add data sources.\n\nIt suits operators who want depth across a full season calendar without running a trading desk per sport.",
    followups: ["amm", "resolver", "launch"]
  },
  amm: {
    text: "Liquidity is usually the first real design decision in a prediction market. Three routes:\n\n• AMM: a bonding curve maker always quotes both sides. Best for long tail and low volume markets where an order book would sit empty. You fund the curve and carry the inventory risk.\n• Order book: users make and take at their own prices. Best for high volume markets with real two sided interest, and the model brokerages and traders expect.\n• Hybrid: an order book with AMM backstop quotes, so thin markets still trade.\n\nMost platforms we build launch hybrid. The AMM seeds every new market and the book takes over as volume arrives. Spread controls, per market exposure caps and liquidity provider accounting ship with it.",
    followups: ["sportiq", "launch", "broker"]
  },
  launch: {
    text: "A white label launch typically runs 8 to 14 weeks, depending on how much is yours versus ours.\n\nRough shape:\n• Weeks 1 to 2: category and market design, branding, integration list.\n• Weeks 3 to 8: white label configuration, KYC and payments integration, data feeds, market templates.\n• Weeks 9 to 12: internal trading, compliance review, load testing at your expected concurrency.\n• Launch, then market coverage widens.\n\nA custom build with your own architecture, exchange mechanics or exotic market types runs longer.",
    followups: ["ownership", "amm", "coverage"]
  },
  broker: {
    text: "Brokerages adding event contracts usually need four things beyond the trading engine:\n\n• An exchange style front end existing traders recognise: order book, depth, positions, running P&L.\n• Contract specification and settlement workflows that survive a compliance review, with the resolution source fixed at listing.\n• Integration into your current identity, funding and reporting stack rather than a separate wallet.\n• Regulatory framing for the contracts you intend to list, which varies by jurisdiction and is where most timelines actually go.\n\nWe deliver this as a custom platform, so your compliance team controls the contract catalogue.",
    followups: ["ownership", "amm", "launch"]
  },
  ownership: {
    text: "Yes. Engagements ship with complete source code ownership on your side, whether you take the white label route or a fully custom build.\n\nWhat that means day to day:\n• Your team can read, extend and audit the platform without waiting on us.\n• No per market or per user licence lock in on the core product.\n• We can stay on for feature work and support, or hand over to your engineers.\n\nData feeds, KYC and payment providers stay on their own commercial terms since those are third party services.",
    followups: ["launch", "coverage", "broker"]
  },
  coverage: {
    text: "Market coverage follows the data you can resolve against. Common categories:\n\n• Sports: fixtures, in play moments, player props, season outcomes, driven by SportIQ.\n• Finance and crypto: price levels, ranges, macro prints and scheduled releases.\n• Politics and current events: elections, appointments, policy milestones.\n• Entertainment and culture: awards, releases, reality formats, brand campaigns.\n\nThe practical limit is a source you can point at before listing. If the outcome cannot be resolved cleanly, the market should not exist.",
    followups: ["creator", "resolver", "amm"]
  },
  "/prediction-market-software": {
    text: "Vinfotech builds white-label and custom prediction market platforms for iGaming operators, brokerages, sports leagues, and media brands.\n\nKey capabilities:\n• Market Engines: Automated Market Makers (AMM), central limit order books (CLOB), or hybrid liquidity.\n• Real-Time Feeds: Automated event creation, live dynamic odds via SportIQ, and instant settlement.\n• Sports, Finance & Culture: Coverage from live match fixtures to macro financial prints and elections.\n• Full Ownership: Complete source code ownership with no per-user or revenue-share licensing lock-in.",
    followups: ["launch", "amm", "sportiq"]
  },
  "/audience-engagement-platform": {
    text: "Vinfotech builds custom Interactive Fan Zones for sports teams, leagues, and media publishers to turn passive audiences into passionate communities.\n\nCore modules:\n• Centralized digital fan portal accessible via web, mobile, or embedded SDK within your main app.\n• Gamified daily challenges, prediction streak games, and social fan walls.\n• Personalized fan profiles with activity history, loyalty tiers, and favorite team badges.\n• Proven track record of boosting repeat visitor sessions by over 300%.",
    followups: ["fan_zones", "matchday", "rewards"]
  },
  "/fantasy-sports-software-development": {
    text: "We design and engineer enterprise fantasy sports platforms trusted by operators and sports leagues worldwide.\n\nWhat we deliver:\n• Diverse Game Formats: Daily Fantasy Sports (DFS), season-long leagues, pick'em, survivor, and custom tournament mechanics.\n• Live Scoring & Stats: Sub-second match data feeds for football, basketball, cricket, baseball, soccer, and more.\n• Player Management & Compliance: Integrated KYC, geo-fencing, wallet management, and secure payment processing.\n• Massive Scale: Tested and optimized to handle thousands of concurrent players during peak match hours.",
    followups: ["dfs", "season_long", "pickem_props"]
  },
  "/white-label-sports-predictor-games": {
    text: "Our Games Catalog features turnkey sports predictor games and interactive gamification formats.\n\nHighlights:\n• Formats: Streak predictors, scoreline predictions, bracket challenges, and trivia mini-games.\n• Engagement: Rapid deployment with full branding customization.\n• Commercial Activations: Turnkey sponsor banners, coupons, and lead generation tools.",
    followups: ["prediction", "matchday", "fan_zones"]
  },
  "/custom-game-development": {
    text: "Our game development team delivers high-performance custom iGaming solutions and betting products.\n\nOfferings:\n• Sportsbook & Betting Engines: Pre-match and real-time in-play betting with robust risk controls.\n• Custom Games: Bespoke casino games, multiplier games, crash games, and RNG engines.\n• Complete Handover: Full source code ownership, regulatory compliance support, and turnkey architecture.",
    followups: ["betting", "software", "ownership"]
  },
  fallback: {
    text: "That sits inside what we build, so here is the useful version.\n\nVinfotech builds white label and custom prediction market platforms for iGaming operators, brokerages, sports leagues and media companies. Market creation, dynamic pricing, matching, settlement and free to play tournament modules, delivered with full source code ownership.\n\nTell me more about what you are planning, the audience, market categories and jurisdiction, and I can be specific.",
    followups: ["launch", "amm", "sportiq"]
  }
};


export function keyForPredictionMarket(text: string): string {
  const t = text.toLowerCase();
  if (/sportiq/.test(t)) return "sportiq";
  if (/source code|own(ership)?|licen/.test(t)) return "ownership";
  if (/categor|cover|which market|what market/.test(t)) return "coverage";
  if (/resolv|settl/.test(t)) return "resolver";
  if (/amm|liquid|order book|pricing model/.test(t)) return "amm";
  if (/creat|generat/.test(t)) return "creator";
  if (/how long|timeline|launch|white.?label|weeks/.test(t)) return "launch";
  if (/broker|event contract|fintech/.test(t)) return "broker";
  return "fallback";
}

/* ────────────────────────────────────────────────────────────────────
 * Generic / Global Flow: Products, Services & Platform Offerings
 * ──────────────────────────────────────────────────────────────────── */

export const GLOBAL_TOPICS: TopicItem[] = [
  { id: "prediction", name: "Prediction Markets", desc: "White-label trading & event contracts" },
  { id: "fantasy", name: "Fantasy Sports", desc: "Daily fantasy, season-long & gaming software" },
  { id: "betting", name: "Sportsbook & iGaming", desc: "Turnkey betting engines & player platforms" },
  { id: "software", name: "Custom Software & Platforms", desc: "Scalable enterprise engineering & digital products" }
];

export const GLOBAL_SUGGESTIONS: SuggestionItem[] = [
  { id: "platforms", text: "What platforms and solutions does Vinfotech build?" },
  { id: "launch", text: "How long does a custom or white-label launch take?" },
  { id: "ownership", text: "Do we own the source code for our platform?" }
];

export const GLOBAL_LABELS: Record<string, string> = {
  ...PREDICTION_MARKET_LABELS,
  platforms: "What platforms and solutions do you build?",
  prediction: "Tell me about Prediction Markets",
  fantasy: "What fantasy sports software do you offer?",
  betting: "What sportsbook & iGaming solutions do you have?",
  software: "Tell me about custom AI & software engineering",
  prediction_intro: "Tell me about Prediction Markets"
};

export const GLOBAL_REPLIES: Record<string, PredictionMarketReply> = {
  platforms: {
    text: "Vinfotech is a specialized technology partner building high-concurrency digital platforms and AI solutions across iGaming, sports, fintech, and custom enterprise software.\n\nOur core platform offerings include:\n• Prediction Markets: White-label and custom trading platforms with AMM, order books, and real-time feeds.\n• Fantasy Sports: Enterprise DFS, season-long, pick'em, and social gaming platforms.\n• Sports Betting & iGaming: High-throughput sportsbook engines, PAM, risk management, and player wallets.\n• Custom AI & Software: Bespoke web/mobile applications, autonomous AI agents, and enterprise integrations.\n\nAll solutions ship with complete intellectual property and source code ownership on your side.",
    followups: ["prediction", "fantasy", "launch"]
  },
  prediction: {
    text: "Vinfotech builds white-label and custom prediction market platforms for iGaming operators, brokerages, sports leagues, and media brands.\n\nKey capabilities:\n• Market Engines: Automated Market Makers (AMM), central limit order books, or hybrid liquidity.\n• Real-Time Feeds: Automated event creation, live dynamic odds, and instant settlement.\n• Sports, Finance & Culture: Coverage from live match fixtures to macro financial prints and elections.\n• Full Ownership: Complete source code ownership with no per-user or revenue-share licensing lock-in.",
    followups: ["launch", "amm", "sportiq"]
  },
  fantasy: {
    text: "We design and engineer enterprise fantasy sports platforms trusted by operators and sports leagues worldwide.\n\nWhat we deliver:\n• Diverse Game Formats: Daily Fantasy Sports (DFS), season-long leagues, pick'em, survivor, and custom tournament mechanics.\n• Live Scoring & Stats: Sub-second match data feeds for football, basketball, cricket, baseball, soccer, and more.\n• Player Management & Compliance: Integrated KYC, geo-fencing, wallet management, and secure payment processing.\n• Massive Scale: Tested and optimized to handle thousands of concurrent players during peak match hours.",
    followups: ["platforms", "launch", "ownership"]
  },
  betting: {
    text: "Our sportsbook and iGaming solutions provide complete technical infrastructure for regulated gaming operators.\n\nWhat it covers:\n• Sportsbook Engine: Pre-match and real-time in-play betting across global sports catalogues.\n• Automated Risk Controls: Margin management, liability limits, and automated trading backstops.\n• Player Account Management (PAM): Unified player profiles, KYC, loyalty rewards, and multi-currency cashier.\n• Native Mobile & Web: High-performance iOS, Android, and web front-ends designed for conversion.",
    followups: ["platforms", "ownership", "launch"]
  },
  software: {
    text: "Beyond specialized platforms, Vinfotech provides bespoke engineering pods and AI agent development for complex digital products.\n\nWhat we do:\n• Architecture & Scaling: Modern cloud architectures designed for resilience and millions of transactions.\n• Generative AI & Automation: Custom AI agents, LLM integrations, and proprietary data pipelines.\n• Product Engineering: Full lifecycle engineering from UX strategy to production deployment.\n• Code Handover: Complete code repository transfer and documentation for your in-house teams.",
    followups: ["platforms", "launch", "ownership"]
  },
  launch: {
    text: "A typical white-label or turnkey platform launch runs between 8 to 14 weeks depending on integrations and custom workflows.\n\nTypical trajectory:\n• Weeks 1–2: Product specification, brand customization, and integration mapping.\n• Weeks 3–8: Core platform configuration, KYC/payment gateways, data feeds, and rules engines.\n• Weeks 9–12: Staging sandbox testing, compliance verification, and high-concurrency load testing.\n• Launch & Handover: Production deployment, operator training, and code repository transfer.",
    followups: ["ownership", "platforms", "prediction"]
  },
  ownership: {
    text: "Yes. Every engagement delivers 100% complete source code ownership to your company.\n\nWhy this matters:\n• You maintain full strategic control without proprietary vendor lock-in.\n• No recurring per-seat or GMV revenue-share royalties on core software.\n• Your internal engineering team can audit, modify, and extend the platform anytime.",
    followups: ["launch", "platforms", "prediction"]
  },
  prediction_intro: {
    text: "Our white-label prediction market platform allows you to launch your own branded prediction exchange in weeks.\n\nIt features automated market creation from live feeds, hybrid liquidity (AMM + order book), automatic settlement, dispute workflows, and compliance-ready architecture, shipped with full source code ownership.",
    followups: ["launch", "amm", "sportiq"]
  },
  "/prediction-market-software": {
    text: "Vinfotech builds white-label and custom prediction market platforms for iGaming operators, brokerages, sports leagues, and media brands.\n\nKey capabilities:\n• Market Engines: Automated Market Makers (AMM), central limit order books (CLOB), or hybrid liquidity.\n• Real-Time Feeds: Automated event creation, live dynamic odds via SportIQ, and instant settlement.\n• Sports, Finance & Culture: Coverage from live match fixtures to macro financial prints and elections.\n• Full Ownership: Complete source code ownership with no per-user or revenue-share licensing lock-in.",
    followups: ["launch", "amm", "sportiq"]
  },
  "/audience-engagement-platform": {
    text: "Vinfotech builds custom Interactive Fan Zones for sports teams, leagues, and media publishers to turn passive audiences into passionate communities.\n\nCore modules:\n• Centralized digital fan portal accessible via web, mobile, or embedded SDK within your main app.\n• Gamified daily challenges, prediction streak games, and social fan walls.\n• Personalized fan profiles with activity history, loyalty tiers, and favorite team badges.\n• Proven track record of boosting repeat visitor sessions by over 300%.",
    followups: ["fan_zones", "matchday", "rewards"]
  },
  "/fantasy-sports-software-development": {
    text: "We design and engineer enterprise fantasy sports platforms trusted by operators and sports leagues worldwide.\n\nWhat we deliver:\n• Diverse Game Formats: Daily Fantasy Sports (DFS), season-long leagues, pick'em, survivor, and custom tournament mechanics.\n• Live Scoring & Stats: Sub-second match data feeds for football, basketball, cricket, baseball, soccer, and more.\n• Player Management & Compliance: Integrated KYC, geo-fencing, wallet management, and secure payment processing.\n• Massive Scale: Tested and optimized to handle thousands of concurrent players during peak match hours.",
    followups: ["dfs", "season_long", "pickem_props"]
  },
  "/white-label-sports-predictor-games": {
    text: "Our Games Catalog features turnkey sports predictor games and interactive gamification formats.\n\nHighlights:\n• Formats: Streak predictors, scoreline predictions, bracket challenges, and trivia mini-games.\n• Engagement: Rapid deployment with full branding customization.\n• Commercial Activations: Turnkey sponsor banners, coupons, and lead generation tools.",
    followups: ["prediction", "matchday", "fan_zones"]
  },
  "/custom-game-development": {
    text: "Our game development team delivers high-performance custom iGaming solutions and betting products.\n\nOfferings:\n• Sportsbook & Betting Engines: Pre-match and real-time in-play betting with robust risk controls.\n• Custom Games: Bespoke casino games, multiplier games, crash games, and RNG engines.\n• Complete Handover: Full source code ownership, regulatory compliance support, and turnkey architecture.",
    followups: ["betting", "software", "ownership"]
  },
  fallback: {
    text: "Vinfotech is a technology partner building scalable platforms across prediction markets, fantasy sports, sports betting, and enterprise AI software.\n\nTell me about what you are planning to build, and I can walk you through our technical architecture, timeline, and capabilities.",
    followups: ["platforms", "prediction", "launch"]
  }
};


export function keyForGeneral(text: string): string {
  const t = text.toLowerCase();
  if (/sportiq|event contract|prediction|predict|amm|market maker|order book/.test(t)) return "prediction";
  if (/fantasy|dfs|pickem|season.long/.test(t)) return "fantasy";
  if (/bet|sportsbook|igaming|wagering|odds|pam|casino/.test(t)) return "betting";
  if (/custom|software|ai|engineer|agent|develop|bespoke|pod/.test(t)) return "software";
  if (/how long|timeline|launch|white.?label|weeks|month/.test(t)) return "launch";
  if (/source code|own(ership)?|licen|intellectual property|ip/.test(t)) return "ownership";
  if (/platform|solution|service|what do you do|what.*build|offer/.test(t)) return "platforms";
  return "fallback";
}

/* ────────────────────────────────────────────────────────────────────
 * Fantasy Sports Flow
 * ──────────────────────────────────────────────────────────────────── */

export const FANTASY_SPORTS_TOPICS: TopicItem[] = [
  { id: "dfs", name: "Daily Fantasy Sports (DFS)", desc: "Salary cap, roster selection & live contests" },
  { id: "season_long", name: "Season-Long & Dynasty", desc: "Draft rooms, leagues, trades & waivers" },
  { id: "pickem_props", name: "Pick'em & Player Props", desc: "Over/under player proposition mechanics" },
  { id: "scoring_engine", name: "Live Scoring Engine", desc: "Sub-second stats feeds & calculations" }
];

export const FANTASY_SPORTS_LABELS: Record<string, string> = {
  ...GLOBAL_LABELS,
  dfs: "Tell me about your Daily Fantasy Sports (DFS) solution",
  season_long: "Tell me about Season-Long & Dynasty fantasy software",
  pickem_props: "How does your Pick'em & Player Props platform work?",
  scoring_engine: "Tell me about your live scoring and real-time stats engine",
  fantasy_launch: "How fast can we launch a custom fantasy sports platform?",
  fantasy_clones: "What features are included in your Dream11 and PrizePicks clones?",
  fantasy_compliance: "How do you handle geo-compliance, KYC, and payments?"
};

export const FANTASY_SPORTS_SUGGESTIONS: SuggestionItem[] = [
  { id: "fantasy_launch", text: "How fast can we launch a custom fantasy sports platform?" },
  { id: "fantasy_clones", text: "What features are included in your Dream11 and PrizePicks clones?" },
  { id: "fantasy_compliance", text: "How do you handle geo-compliance, KYC, and payments?" }
];

export const FANTASY_SPORTS_REPLIES: Record<string, PredictionMarketReply> = {
  dfs: {
    text: "Vinfotech delivers enterprise Daily Fantasy Sports (DFS) platforms engineered for massive concurrency and peak game-day traffic.\n\nKey capabilities:\n• Classic salary-cap contests, tiered matchups, head-to-head, and multi-entry guaranteed prize pools (GPP).\n• Real-time roster validation, player salary algorithms, and dynamic entry fee management.\n• Turnkey mobile apps for iOS and Android with intuitive lineup creation and live contest tracking.\n• Full source code ownership with complete customization for your target sports and jurisdictions.",
    followups: ["scoring_engine", "pickem_props", "fantasy_launch"]
  },
  season_long: {
    text: "Our season-long fantasy sports software powers private and public leagues across football, cricket, basketball, baseball, soccer, and motorsport.\n\nWhat it includes:\n• Live snake and auction draft rooms with automated auto-pick and timer controls.\n• Comprehensive league commissioner controls: custom scoring rules, trade reviews, and playoff brackets.\n• Automated waiver wire bidding (FAAB), free agent claims, and injury tracking feeds.\n• Social leaderboards, league chat, and gamified trophies to keep users engaged all season.",
    followups: ["dfs", "scoring_engine", "fantasy_compliance"]
  },
  pickem_props: {
    text: "We build modern Pick'em and Player Prop prediction platforms inspired by top market leaders like PrizePicks and Underdog Fantasy.\n\nHighlights:\n• Simple over/under player projection mechanics across points, rebounds, touchdowns, and wickets.\n• Flex play and power play multipliers with configurable payouts and risk controls.\n• Real-time player projection feeds integrated with official sports statistics providers.\n• Rapid white-label deployment in 8 to 12 weeks with customized brand identity and payment rails.",
    followups: ["fantasy_clones", "dfs", "fantasy_launch"]
  },
  scoring_engine: {
    text: "Our proprietary live scoring engine processes official match data feeds with sub-second latency to compute player fantasy points instantly.\n\nArchitecture highlights:\n• Multi-provider feed ingestion with automated failover and data reconciliation.\n• High-throughput event processing handling thousands of point adjustments simultaneously.\n• Instant push notifications for fantasy scores, lineup injuries, and contest rank changes.\n• Historical match archive and detailed player statistical deep-dives.",
    followups: ["dfs", "season_long", "fantasy_clones"]
  },
  fantasy_launch: {
    text: "A white-label fantasy sports platform can launch in 8 to 12 weeks, while a fully bespoke build typically spans 12 to 16 weeks.\n\nEngagement phases:\n• Weeks 1–2: Requirements scoping, UI/UX brand customization, and game format selection.\n• Weeks 3–8: Core engine configuration, payment gateways, KYC, and data feed integrations.\n• Weeks 9–12: Concurrency stress testing, staging audits, and App Store / Google Play submission.\n• Complete handover with 100% source code ownership and technical documentation.",
    followups: ["dfs", "pickem_props", "fantasy_compliance"]
  },
  fantasy_clones: {
    text: "We provide proven, production-grade clone architectures inspired by industry leaders including Dream11, PrizePicks, DraftKings, and Sleeper.\n\nIncluded out-of-the-box:\n• Complete user experience and game mechanics matching the target platform.\n• Multi-sport support: Cricket, NFL, NBA, Premier League, MLB, Esports, and more.\n• Scalable microservices backend designed to handle 50,000+ concurrent live lineup updates.\n• 100% intellectual property transfer with no recurring revenue-share fees.",
    followups: ["pickem_props", "dfs", "fantasy_launch"]
  },
  fantasy_compliance: {
    text: "Compliance and player safety are built directly into our platform architecture for real-money gaming.\n\nBuilt-in modules:\n• Geo-fencing integration (GeoComply / Radar) to enforce jurisdiction legality.\n• Automated KYC / AML verification flows with tier-1 verification providers.\n• Multi-currency wallet, local payment gateways (UPI, Cards, Crypto, Wallets), and tax deduction (TDS) automation.\n• Responsible gaming features: deposit limits, cooling-off periods, and self-exclusion.",
    followups: ["fantasy_launch", "dfs", "scoring_engine"]
  },
  fallback: {
    text: "Vinfotech is a global technology leader in fantasy sports software development, powering DFS, season-long, pick'em, and clone platforms for operators worldwide.\n\nTell me about the fantasy format, sports, and markets you are targeting, and I will share technical architecture, timeline, and demo access.",
    followups: ["dfs", "pickem_props", "fantasy_launch"]
  }
};

export function keyForFantasy(text: string): string {
  const t = text.toLowerCase();
  if (/dfs|daily fantasy|salary cap|roster|contest|gpp/.test(t)) return "dfs";
  if (/season|dynasty|draft room|snake|auction|waiver|commissioner/.test(t)) return "season_long";
  if (/pick.?em|prop|over.?under|more.?less|prizepicks|underdog/.test(t)) return "pickem_props";
  if (/scoring|score|stat|feed|latency|real.?time/.test(t)) return "scoring_engine";
  if (/how long|timeline|launch|weeks|turnaround/.test(t)) return "fantasy_launch";
  if (/clone|dream11|draftkings|sleeper|feature/.test(t)) return "fantasy_clones";
  if (/compliance|kyc|geo|legal|wallet|tax|tds|licen/.test(t)) return "fantasy_compliance";
  return "fallback";
}

/* ────────────────────────────────────────────────────────────────────
 * Fan Engagement & Interactive Fan Zones Flow
 * ──────────────────────────────────────────────────────────────────── */

export const FAN_ENGAGEMENT_TOPICS: TopicItem[] = [
  { id: "fan_zones", name: "Interactive Fan Zones", desc: "Gamified hubs for leagues, clubs & media" },
  { id: "matchday", name: "Matchday Quizzes & Trivia", desc: "Live in-venue and broadcast fan challenges" },
  { id: "rewards", name: "Loyalty & Digital Rewards", desc: "Fan leaderboards, badges, points & redemption" },
  { id: "sponsor_activations", name: "Brand & Sponsor Activations", desc: "Sponsored mini-games & brand engagement" }
];

export const FAN_ENGAGEMENT_LABELS: Record<string, string> = {
  ...GLOBAL_LABELS,
  fan_zones: "Tell me about your Interactive Fan Zones platform",
  matchday: "How do matchday trivia and live predictors work?",
  rewards: "Tell me about your loyalty and reward mechanics",
  sponsor_activations: "How do brand and sponsor activations work?",
  fan_retention: "How do Interactive Fan Zones drive 3x user retention?",
  fan_monetization: "How can brands and leagues monetize fan engagement?",
  fan_integration: "Can Fan Zones integrate into existing apps and websites?"
};

export const FAN_ENGAGEMENT_SUGGESTIONS: SuggestionItem[] = [
  { id: "fan_retention", text: "How do Interactive Fan Zones drive 3x user retention?" },
  { id: "fan_monetization", text: "How can brands and leagues monetize fan engagement?" },
  { id: "fan_integration", text: "Can Fan Zones integrate into existing apps and websites?" }
];

export const FAN_ENGAGEMENT_REPLIES: Record<string, PredictionMarketReply> = {
  fan_zones: {
    text: "Vinfotech builds custom Interactive Fan Zones for sports teams, leagues, and media publishers to turn passive audiences into passionate communities.\n\nCore modules:\n• Centralized digital fan portal accessible via web, mobile, or embedded SDK within your main app.\n• Gamified daily challenges, prediction streak games, and social fan walls.\n• Personalized fan profiles with activity history, loyalty tiers, and favorite team badges.\n• Proven track record of boosting repeat visitor sessions by over 300%.",
    followups: ["matchday", "rewards", "fan_monetization"]
  },
  matchday: {
    text: "Our Matchday Engagement suite powers live interactive experiences during pre-match build-up, half-time, and live play.\n\nWhat we deliver:\n• Real-time match prediction games: first scorer, match scoreline, and player performance polls.\n• Synchronized speed trivia and live stadium quizzes with big-screen leaderboard integration.\n• Instant fan voting for 'Player of the Match' with real-time graphical results.\n• Works seamlessly on stadium Wi-Fi and mobile 4G/5G networks without app downloads.",
    followups: ["fan_zones", "sponsor_activations", "rewards"]
  },
  rewards: {
    text: "Engage fans with gamified loyalty programs that reward every interaction on your platform.\n\nKey features:\n• XP, points, and achievement badge mechanics for attendance, quiz participation, and content sharing.\n• Digital and physical rewards catalog: merchandise discounts, VIP match tickets, and digital collectibles.\n• Tiered VIP memberships with unlockable exclusive content and player meet-and-greet entries.\n• Complete administrative tools for points issuance, expiry rules, and partner vendor redemption.",
    followups: ["sponsor_activations", "fan_zones", "fan_retention"]
  },
  sponsor_activations: {
    text: "Transform traditional sponsorship into high-performing digital marketing activations with measurable ROI.\n\nSponsor integration features:\n• White-labeled branded mini-games and sponsored prediction challenges with customized sponsor themes.\n• Opt-in 1st-party data and lead capture with GDPR/CCPA-compliant privacy workflows.\n• Dynamic banner ad placements, sponsored video rewards, and custom coupon delivery.\n• Detailed sponsor analytics dashboards displaying impressions, click-through rates, and leads.",
    followups: ["fan_zones", "rewards", "fan_monetization"]
  },
  fan_retention: {
    text: "Interactive Fan Zones drive retention by creating a year-round relationship with fans beyond just the 90 minutes of matchday.\n\nRetention drivers:\n• Daily streak rewards and off-season prediction leagues.\n• Community leaderboards where fans compete against friends and global fan clubs.\n• Push notification triggers for upcoming fixtures, score updates, and expiring rewards.",
    followups: ["fan_zones", "matchday", "fan_integration"]
  },
  fan_monetization: {
    text: "Monetize your fan base through multiple high-margin revenue streams:\n\n• Premium digital club memberships and exclusive content subscriptions.\n• Direct commercial sponsor activations with verified first-party user acquisition.\n• E-commerce integration for official merchandise, ticketing, and digital partner vouchers.",
    followups: ["sponsor_activations", "rewards", "fan_zones"]
  },
  fan_integration: {
    text: "Yes. Our Fan Zone solutions are built with an API-first and SDK-first architecture for flexible integration:\n\n• Web iFrame or React/Vue embed for existing official club websites.\n• Native iOS and Android SDKs for embedding directly into your official club mobile apps.\n• Single Sign-On (SSO) integration with your existing CRM, ticketing system, or member database.",
    followups: ["fan_zones", "matchday", "rewards"]
  },
  fallback: {
    text: "Vinfotech creates interactive fan engagement platforms and fan zones for sports leagues, clubs, brands, and broadcast media worldwide.\n\nTell me about your audience size, sports properties, and goals, and I can walk you through tailored interactive gamification strategies.",
    followups: ["fan_zones", "matchday", "sponsor_activations"]
  }
};

export function keyForFanEngagement(text: string): string {
  const t = text.toLowerCase();
  if (/fan.?zone|hub|portal|community/.test(t)) return "fan_zones";
  if (/matchday|trivia|quiz|poll|in.?venue|stadium/.test(t)) return "matchday";
  if (/reward|loyalty|badge|point|tier|xp|merchandise/.test(t)) return "rewards";
  if (/sponsor|brand|activation|ad |lead|commercial/.test(t)) return "sponsor_activations";
  if (/retention|churn|engagement|daily/.test(t)) return "fan_retention";
  if (/monetiz|revenue|subscription|sponsor/.test(t)) return "fan_monetization";
  if (/integrat|sdk|iframe|app|sso|api/.test(t)) return "fan_integration";
  return "fallback";
}

/* ────────────────────────────────────────────────────────────────────
 * iGaming & Sportsbook Solutions Flow
 * ──────────────────────────────────────────────────────────────────── */

export const IGAMING_SPORTSBOOK_TOPICS: TopicItem[] = [
  { id: "turnkey_sportsbook", name: "Turnkey Sportsbook Engine", desc: "Pre-match & live in-play wagering systems" },
  { id: "predictor_games", name: "Sports Predictor Games", desc: "Streak, jackpot & score predictors" },
  { id: "pam_wallet", name: "Player Account & Wallet (PAM)", desc: "Cashier, KYC, fraud protection & limits" },
  { id: "custom_igaming", name: "Custom Casino & iGaming", desc: "Bespoke RNG titles, crash & instant games" }
];

export const IGAMING_SPORTSBOOK_LABELS: Record<string, string> = {
  ...GLOBAL_LABELS,
  turnkey_sportsbook: "Tell me about your Turnkey Sportsbook platform",
  predictor_games: "Tell me about your Sports Predictor and casual games",
  pam_wallet: "How does your Player Account Management (PAM) system work?",
  custom_igaming: "Tell me about your custom iGaming and casino game development",
  sportsbook_odds: "What sports feeds and trading algorithms do you support?",
  sportsbook_licensing: "Do we get 100% full source code ownership for our sportsbook?",
  sportsbook_timeline: "What is the launch timeline for a turnkey sportsbook platform?"
};

export const IGAMING_SPORTSBOOK_SUGGESTIONS: SuggestionItem[] = [
  { id: "sportsbook_odds", text: "What sports feeds and trading algorithms do you support?" },
  { id: "sportsbook_licensing", text: "Do we get 100% full source code ownership for our sportsbook?" },
  { id: "sportsbook_timeline", text: "What is the launch timeline for a turnkey sportsbook platform?" }
];

export const IGAMING_SPORTSBOOK_REPLIES: Record<string, PredictionMarketReply> = {
  turnkey_sportsbook: {
    text: "Vinfotech delivers high-performance turnkey and bespoke sportsbook engineering for regulated gaming operators.\n\nPlatform capabilities:\n• Extensive sports coverage: 60+ sports, 1,000+ leagues, and thousands of pre-match & in-play live markets.\n• Automated risk management: real-time exposure monitoring, margin controls, and automated odds pricing.\n• Fast, responsive frontends for web and native iOS/Android built for high-stakes conversion.\n• Full compliance readiness with multi-jurisdictional reporting and audit logging.",
    followups: ["pam_wallet", "predictor_games", "sportsbook_odds"]
  },
  predictor_games: {
    text: "Our sports predictor games catalog offers high-retention, casual betting formats for sportsbooks and media brands.\n\nGame formats:\n• Streak Predictor: predict consecutive match outcomes to build winning multipliers.\n• Super 6 / Score Predictor: predict correct scorelines for headline weekend fixtures.\n• Bracket Challenges: tournament knockout predictors for World Cup, Champions League, March Madness.\n• Available as free-to-play marketing funnels or integrated real-money betting modules.",
    followups: ["turnkey_sportsbook", "pam_wallet", "custom_igaming"]
  },
  pam_wallet: {
    text: "Our Player Account Management (PAM) and wallet core provides the complete operational backbone for iGaming platforms.\n\nKey features:\n• Unified player ledger with multi-currency, bonus balances, and automated wagering turnover calculations.\n• Flexible payment gateway integration supporting fiat cashiers, bank rails, and crypto options.\n• Automated KYC/AML verification, biometric login, and responsible gaming limits (deposit, loss, timeout).\n• Comprehensive Backoffice: bonus engine, affiliate management, and customer segmentation.",
    followups: ["turnkey_sportsbook", "custom_igaming", "sportsbook_licensing"]
  },
  custom_igaming: {
    text: "We engineer bespoke casino and iGaming titles that differentiate your brand in saturated markets.\n\nWhat we develop:\n• Crash Games: high-octane multiplayer multiplier games with social chat and auto-cashout.\n• Instant Win & Plinko: fast-paced casual wagering titles certified by independent test labs (GLI, iTech).\n• Tailored UI/UX skins, animations, and sound design created by our dedicated gaming studio.\n• Seamless integration via standard iGaming RGS (Remote Gaming Server) protocols.",
    followups: ["turnkey_sportsbook", "pam_wallet", "predictor_games"]
  },
  sportsbook_odds: {
    text: "We integrate with leading tier-1 sports data providers (Sportradar, Betradar, Genius Sports, Stats Perform, etc.) with automated feed normalization and fallback failover.\n\nOur odds engine allows operators to customize margins by sport, league, or market type.",
    followups: ["turnkey_sportsbook", "sportsbook_timeline", "sportsbook_licensing"]
  },
  sportsbook_licensing: {
    text: "Yes. Vinfotech engagements provide 100% complete source code ownership to your company.\n\nYou avoid perpetual GMV-based revenue shares or per-player license locks, giving your business full control over its technology asset.",
    followups: ["turnkey_sportsbook", "sportsbook_timeline", "pam_wallet"]
  },
  sportsbook_timeline: {
    text: "A turnkey sportsbook deployment typically takes 10 to 14 weeks from initial kickoff to live launch, including payment gateway, KYC, and odds feed integration.",
    followups: ["turnkey_sportsbook", "pam_wallet", "sportsbook_licensing"]
  },
  fallback: {
    text: "Vinfotech is an enterprise iGaming and sportsbook technology partner, building scalable wagering engines, PAM wallets, and custom game platforms.\n\nTell me about your target markets and licensing requirements, and I can detail our architecture and roadmap.",
    followups: ["turnkey_sportsbook", "predictor_games", "pam_wallet"]
  }
};

export function keyForIGaming(text: string): string {
  const t = text.toLowerCase();
  if (/sportsbook|wagering|betting|engine|odds|margin/.test(t)) return "turnkey_sportsbook";
  if (/predictor|streak|super 6|bracket|score/.test(t)) return "predictor_games";
  if (/pam|wallet|cashier|kyc|bonus|player account|deposit/.test(t)) return "pam_wallet";
  if (/casino|crash|rng|plinko|instant win|game/.test(t)) return "custom_igaming";
  if (/feed|provider|sportradar|betradar|genius/.test(t)) return "sportsbook_odds";
  if (/ownership|code|licen|revenue share|gmv/.test(t)) return "sportsbook_licensing";
  if (/how long|timeline|launch|weeks/.test(t)) return "sportsbook_timeline";
  return "fallback";
}

/* ────────────────────────────────────────────────────────────────────
 * Multi-Context Registry & Page Detector
 * ──────────────────────────────────────────────────────────────────── */

export type AdvisorContextType =
  | 'general'
  | 'prediction-market'
  | 'fantasy-sports'
  | 'fan-engagement'
  | 'igaming-sportsbook';

export interface AdvisorContextConfig {
  id: AdvisorContextType;
  badgeLabel: string;
  heading: string;
  subheading: string;
  placeholder: string;
  topics: TopicItem[];
  suggestions: SuggestionItem[];
  labels: Record<string, string>;
  replies: Record<string, PredictionMarketReply>;
  keyForQuery: (text: string) => string;
}

export const ADVISOR_CONTEXTS: Record<AdvisorContextType, AdvisorContextConfig> = {
  general: {
    id: 'general',
    badgeLabel: 'Vinfotech Product Expert',
    heading: 'What would you like to know?',
    subheading: 'Start with a question below, or ask your own about our products and services.',
    placeholder: 'Ask the Vinfotech Product Expert',
    topics: GLOBAL_TOPICS,
    suggestions: GLOBAL_SUGGESTIONS,
    labels: GLOBAL_LABELS,
    replies: GLOBAL_REPLIES,
    keyForQuery: keyForGeneral
  },
  'prediction-market': {
    id: 'prediction-market',
    badgeLabel: 'Prediction Market Advisor',
    heading: 'What would you like to know?',
    subheading: 'Start with a question below, or ask your own.',
    placeholder: 'Ask the Prediction Market Advisor',
    topics: PREDICTION_MARKET_TOPICS,
    suggestions: PREDICTION_MARKET_SUGGESTIONS,
    labels: PREDICTION_MARKET_LABELS,
    replies: PREDICTION_MARKET_REPLIES,
    keyForQuery: keyForPredictionMarket
  },
  'fantasy-sports': {
    id: 'fantasy-sports',
    badgeLabel: 'Fantasy Sports Advisor',
    heading: 'What would you like to know?',
    subheading: 'Start with a question below, or ask your own about fantasy sports software.',
    placeholder: 'Ask the Fantasy Sports Advisor',
    topics: FANTASY_SPORTS_TOPICS,
    suggestions: FANTASY_SPORTS_SUGGESTIONS,
    labels: FANTASY_SPORTS_LABELS,
    replies: FANTASY_SPORTS_REPLIES,
    keyForQuery: keyForFantasy
  },
  'fan-engagement': {
    id: 'fan-engagement',
    badgeLabel: 'Fan Engagement Advisor',
    heading: 'What would you like to know?',
    subheading: 'Start with a question below, or ask your own about fan zones and engagement solutions.',
    placeholder: 'Ask the Fan Engagement Advisor',
    topics: FAN_ENGAGEMENT_TOPICS,
    suggestions: FAN_ENGAGEMENT_SUGGESTIONS,
    labels: FAN_ENGAGEMENT_LABELS,
    replies: FAN_ENGAGEMENT_REPLIES,
    keyForQuery: keyForFanEngagement
  },
  'igaming-sportsbook': {
    id: 'igaming-sportsbook',
    badgeLabel: 'iGaming & Sportsbook Advisor',
    heading: 'What would you like to know?',
    subheading: 'Start with a question below, or ask your own about sportsbook engines and iGaming platforms.',
    placeholder: 'Ask the iGaming & Sportsbook Advisor',
    topics: IGAMING_SPORTSBOOK_TOPICS,
    suggestions: IGAMING_SPORTSBOOK_SUGGESTIONS,
    labels: IGAMING_SPORTSBOOK_LABELS,
    replies: IGAMING_SPORTSBOOK_REPLIES,
    keyForQuery: keyForIGaming
  }
};

/**
 * Detects which product and service category the active page belongs to,
 * selecting the matching Advisor flow and Explore topics.
 */
export function detectAdvisorContext(
  overrideContext?: string,
  pathname?: string,
  search?: string,
  title?: string
): AdvisorContextType {
  // 1. Explicit override passed via props or config
  if (overrideContext === 'prediction-market' || overrideContext === 'prediction') {
    return 'prediction-market';
  }
  if (overrideContext === 'fantasy-sports' || overrideContext === 'fantasy') {
    return 'fantasy-sports';
  }
  if (overrideContext === 'fan-engagement' || overrideContext === 'fan-zones') {
    return 'fan-engagement';
  }
  if (overrideContext === 'igaming-sportsbook' || overrideContext === 'igaming' || overrideContext === 'sportsbook') {
    return 'igaming-sportsbook';
  }
  if (overrideContext === 'general') {
    return 'general';
  }

  // 2. Read from browser window if available
  const rawPath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const path = rawPath.toLowerCase().replace(/\/+$/, '');
  const query = (search ?? (typeof window !== 'undefined' ? window.location.search : '')).toLowerCase();
  const pageTitle = (title ?? (typeof document !== 'undefined' ? document.title : '')).toLowerCase();

  // If strictly on Home Page, ALWAYS return 'general' (Vinfotech Product Expert)
  const isHomePage = path === '' || path === '/' || path === '/home' || path === '/index.html' || path === '/index.php';
  if (isHomePage && !query.includes('topic=') && !query.includes('silo=')) {
    return 'general';
  }

  // Check URL query parameters (e.g. ?topic=... or ?silo=...)
  if (
    query.includes('prediction-market') ||
    query.includes('prediction') ||
    query.includes('predictor') ||
    query.includes('sportiq')
  ) {
    return 'prediction-market';
  }
  if (query.includes('fantasy') || query.includes('dfs') || query.includes('prizepicks') || query.includes('dream11')) {
    return 'fantasy-sports';
  }
  if (query.includes('fan-zone') || query.includes('fan-engagement') || query.includes('audience-engagement')) {
    return 'fan-engagement';
  }
  if (query.includes('sportsbook') || query.includes('igaming')) {
    return 'igaming-sportsbook';
  }

  // Check URL path for Prediction Markets:
  if (
    path.includes('prediction-market') ||
    path.includes('prediction-markets') ||
    path.includes('white-label-prediction-market') ||
    path.includes('crypto-prediction-market') ||
    path.includes('event-contract') ||
    path.includes('polymarket-clone') ||
    path.includes('kalshi-clone') ||
    path.includes('opinion-trading') ||
    path.includes('ai-market-creator') ||
    path.includes('prediction-market-resolver') ||
    path.includes('sportiq') ||
    path.includes('vsprex') ||
    path.includes('sports-prediction-markets') ||
    path.includes('white-label-sports-predictor-games') ||
    path.includes('sports-predictor')
  ) {
    return 'prediction-market';
  }

  // Check URL path for Fantasy Sports (e.g. Dream11, PrizePicks, DFS, Season-long):
  if (
    path.includes('fantasy-sports') ||
    path.includes('fantasy-sports-app-development') ||
    path.includes('fantasy-sports-software-development') ||
    path.includes('dream11-clone') ||
    path.includes('prizepicks-clone') ||
    path.includes('draftkings-clone') ||
    path.includes('probo-clone') ||
    path.includes('props-fantasy') ||
    path.includes('white-label-daily-fantasy') ||
    path.includes('season-long-fantasy') ||
    path.includes('fantasy-stock-market') ||
    path.includes('fantasy-super-app') ||
    path.includes('fantasy-reality-tv') ||
    path.includes('dfs') ||
    path.includes('fantasy')
  ) {
    return 'fantasy-sports';
  }

  // Check URL path for Fan Engagement / Interactive Fan Zones:
  if (
    path.includes('audience-engagement') ||
    path.includes('matchday-fan-engagement') ||
    path.includes('customer-engagement') ||
    path.includes('employee-engagement') ||
    path.includes('engage360') ||
    path.includes('fan-zone') ||
    path.includes('fan-zones')
  ) {
    return 'fan-engagement';
  }

  // Check URL path for iGaming & Sportsbook:
  if (
    path.includes('sportsbook-solutions') ||
    path.includes('igaming-platform') ||
    path.includes('igaming-software') ||
    path.includes('custom-game-development') ||
    path.includes('sports-prediction-game-development') ||
    path.includes('igaming') ||
    path.includes('sportsbook')
  ) {
    return 'igaming-sportsbook';
  }

  // Check document title only on subpages (not home):
  if (
    pageTitle.includes('prediction market') ||
    pageTitle.includes('sports predictor') ||
    pageTitle.includes('event contract') ||
    pageTitle.includes('polymarket') ||
    pageTitle.includes('kalshi')
  ) {
    return 'prediction-market';
  }
  if (
    pageTitle.includes('fantasy sports') ||
    pageTitle.includes('dream11') ||
    pageTitle.includes('prizepicks') ||
    pageTitle.includes('draftkings') ||
    pageTitle.includes('daily fantasy') ||
    pageTitle.includes('fantasy')
  ) {
    return 'fantasy-sports';
  }
  if (
    pageTitle.includes('fan zone') ||
    pageTitle.includes('audience engagement') ||
    pageTitle.includes('fan engagement')
  ) {
    return 'fan-engagement';
  }
  if (
    pageTitle.includes('sportsbook') ||
    pageTitle.includes('igaming')
  ) {
    return 'igaming-sportsbook';
  }

  // Default to the generic/global flow for home and other pages
  return 'general';
}
